import { Box, Grid, Stack, Typography } from '@mui/material';
import AppleSignup from 'components/create-account/apple-signup/apple-signup.component';
import GoogleSignUp from 'components/create-account/google-signup/google-signup.component';
import Allergy from 'components/dietary-preferences/allergy/allergy.component';
import Dietary from 'components/dietary-preferences/dietary/dietary.component';
import BirthMonthDaySelect from 'components/form/form-fields/birth-month-day-select/birth-month-day-select.component';
import ConfirmPassword from 'components/form/form-fields/confirm-password/confirm-password.component';
import Email from 'components/form/form-fields/email.component';
import FirstName from 'components/form/form-fields/first-name.component';
import LastName from 'components/form/form-fields/last-name.component';
import Password from 'components/form/form-fields/password/password.component';
import MandatoryFieldsInstruction from 'components/form/mandatory-fields-instruction/mandatory-fields-instruction.component';
import TocAndPrivacy from 'components/toc-and-privacy/toc-and-privacy.component';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { SMS_CODE, SMS_LINK } from 'redux/config/config.constants';
import {
  selectClientName,
  selectIsDateOfBirthEnabled,
  selectSocialSignInEnabled,
  selectUserAccountVerificationMethod,
} from 'redux/config/config.selectors';
import { signUpUserStartAsync } from 'redux/user/user.actions.js';
import { getDateOfBirth } from 'utils';
import { selectIsLoading } from '../../redux/user/user.selectors';
import CustomCheckbox from '../custom-checkbox/custom-checkbox.component';
import FormError from '../form-error/form-error.component';
import Form from '../form/form.component';
import InputPhone from '../input-phone/input-phone.component';
import Spinner from '../spinner/spinner.component';
import ReferralCode from './referral-code/referral-code.component';
import * as S from './register-fields.styles';
import RegisterFieldCheckboxLabel from './register-field-checkbox-label/register-field-checkbox-label.component';

const RegisterFields = ({
  onSuccess,
  registerData,
  send,
  submitAction,
  showPasswordFields = !registerData,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isLoading = useSelector(selectIsLoading);
  const clientName = useSelector(selectClientName);
  const isDateOfBirthEnabled = useSelector(selectIsDateOfBirthEnabled);
  const socialLogin = useSelector(selectSocialSignInEnabled);
  const verificationMethod = useSelector(selectUserAccountVerificationMethod);
  const isSmsVerification = [SMS_CODE, SMS_LINK].includes(verificationMethod);

  const [error, setError] = useState();
  const isCheckout = useRouteMatch(['/checkout']);

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      ...(registerData?.userData ?? {}),
      birthMonth: '',
      birthDay: '',
    },
  });

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    setValue,
  } = methods;

  useEffect(() => {
    if (!registerData?.userData) return;
    setValue('firstName', registerData.userData.firstName);
    setValue('lastName', registerData.userData.lastName);
    setValue('email', registerData.userData.email);
  }, [setValue, registerData]);

  const onSubmit = async formData => {
    setError(undefined);
    const { birthMonth, birthDay, ...data } = formData;
    const newData = {
      ...data,
      dateOfBirth: getDateOfBirth(birthMonth, birthDay),
      password: !!registerData?.userData?.firstName ? 'null' : data.password,
      jwt: registerData?.jwt,
      authenticationProvider: registerData?.authProvider,
    };

    if (!!submitAction) {
      const response = await submitAction(newData);
      if (!!response?.error) setError(response.error);

      return response;
    }

    const response = await dispatch(signUpUserStartAsync(newData));

    const obj = !!registerData?.userData?.firstName
      ? {
          ...response.data,
          email: newData.email,
          phoneNumber: newData.phoneNumber,
          password: newData.password,
          authenticationProvider: newData.authenticationProvider,
          jwt: newData.jwt,
        }
      : {
          ...response.data,
          email: data.email,
          phoneNumber: data.phoneNumber,
          password: data.password,
        };

    if (!!response?.data) {
      onSuccess(obj);
    } else {
      setError(response);
    }
  };

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {isLoading && !isCheckout && <Spinner fade={false} />}
        {!!socialLogin && !registerData && (
          <>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <GoogleSignUp
                  btnText={t('authOptions.label.googleSignUp')}
                  send={send}
                  setError={setError}
                />
              </Grid>
              <Grid item xs={12}>
                <AppleSignup
                  btnText={t('authOptions.label.appleSignUp')}
                  send={send}
                  setError={setError}
                />
              </Grid>
            </Grid>

            <Box mt={1}>
              <Typography variant="h6">
                {t('register.label.personalInfo')}
              </Typography>
            </Box>
            <S.HorizontalLine />
          </>
        )}

        <MandatoryFieldsInstruction />

        <FirstName register={register} errors={errors} />
        <LastName register={register} errors={errors} />
        <Email
          register={register}
          errors={errors}
          disabled={!!registerData?.userData?.email}
        />
        {showPasswordFields && (
          <>
            <Password />
            <ConfirmPassword />
          </>
        )}
        <InputPhone
          disabled={!!registerData?.userData?.phoneNumber}
          defaultValue={registerData?.userData?.phoneNumber}
        />
        {isDateOfBirthEnabled && <BirthMonthDaySelect />}
        <Allergy />
        <Dietary />
        <ReferralCode register={register} />
        <Stack spacing={0.5}>
          <Typography>{t('register.label.newsletterSubscribe')}</Typography>
          <CustomCheckbox
            label={
              <RegisterFieldCheckboxLabel
                label={t('register.label.emailOptIn')}
                helperText={t('register.label.emailOptIn2', { clientName })}
              />
            }
            {...register('EMAIL_OPT_IN', {
              required: false,
            })}
            dataTestId="RegisterFields.CustomCheckbox.Subscribe"
            sx={{ alignItems: 'flex-start' }}
            inputProps={{
              'aria-label': `${t('register.label.emailOptIn')}. ${t(
                'register.label.emailOptIn2',
                { clientName },
              )}`,
            }}
          />
          <CustomCheckbox
            label={
              <RegisterFieldCheckboxLabel
                label={t('register.label.smsMarketingOptIn')}
                helperText={t('register.label.smsMarketingOptIn2', {
                  clientName,
                })}
              />
            }
            {...register('SMS_MARKETING_OPT_IN', {
              required: false,
            })}
            dataTestId="RegisterFields.CustomCheckbox.SmsMarketing"
            sx={{ alignItems: 'flex-start' }}
            inputProps={{
              'aria-label': `${t('register.label.smsMarketingOptIn')}. ${t(
                'register.label.smsMarketingOptIn2',
                { clientName },
              )}`,
            }}
          />
          <CustomCheckbox
            label={
              <RegisterFieldCheckboxLabel
                label={t('register.label.smsNotificationOptIn')}
                helperText={t('register.label.smsNotificationOptIn2', {
                  clientName,
                })}
              />
            }
            {...register('SMS_TXN_OPT_IN', {
              required: false,
            })}
            dataTestId="RegisterFields.CustomCheckbox.SmsNotifications"
            sx={{ alignItems: 'flex-start' }}
            inputProps={{
              'aria-label': `${t('register.label.smsNotificationOptIn')}. ${t(
                'register.label.smsNotificationOptIn2',
                { clientName },
              )}`,
            }}
          />
          <Typography fontSize="0.7rem" fontStyle="italic" textAlign="center">
            {t('register.label.smsInstructions')}
          </Typography>
        </Stack>
        {error && <FormError errorMessage={error} />}
        <S.SubmitButton
          type="submit"
          data-testid="RegisterFields.CustomButton.Submit"
          fullWidth
          disabled={isSubmitting}
        >
          {t('register.label.createAccount')}
        </S.SubmitButton>
        <TocAndPrivacy
          description={t(
            isSmsVerification
              ? 'register.termsAndConditions.smsDescription'
              : 'register.termsAndConditions.description',
          )}
          postText={
            isSmsVerification
              ? t('register.termsAndConditions.smsDescriptionPostText')
              : undefined
          }
        />
      </Form>
    </FormProvider>
  );
};

export default RegisterFields;
